/**
 * KartotekaListViewsPanel — zakładka „Widoki list" z zarządzaniem nazwanymi widokami listy
 * biblioteki kartoteki (`kartoteka_views`, viewType='list'): dodaj / zmień nazwę (ołówek) /
 * duplikuj / usuń (guard publikacji). Lustro `KartotekaFormViewsPanel`, inny typ widoku.
 *
 * Nowy widok startuje z DOMYŚLNYM `ListConfig` kartoteki (`defaultListConfig` — kolumny
 * slug-driven z pól showInList; walidowane przez backend). Edycja kolumn/filtrów w builderze
 * listy to osobny krok (builder nie jest dziś osadzalny w scope admina — patrz task).
 *
 * Zero window.confirm — usuwanie przez `<ConfirmDialog variant="danger">` (CLAUDE.md §4.12).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { KartotekaViewDto } from '@saas/shared';
import { useMutation, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Copy, Eye, Pencil, Plus, Trash2 } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { Skeleton } from '@/components/Skeleton';
import { Tooltip } from '@/components/Tooltip';
import { useToast } from '@/contexts/ToastContext';
import {
  createKartotekaLibraryView,
  deleteKartotekaLibraryView,
  type KartotekaBuilderField,
  type KartotekaDefaultListConfig,
  type KartotekaIdentityRef,
  kartotekaLibraryViewsQueryKey,
  updateKartotekaLibraryView,
} from '@/lib/kartoteki/kartoteka-definition';

import {
  CreateKartotekaViewModal,
  nameToKartotekaViewSlug,
  SLUG_MAX_LENGTH,
} from './CreateKartotekaViewModal';
import { KartotekaViewListBuilder } from './KartotekaViewListBuilder';

/** Dokleja `_2`, `_3`, … aż slug bazowy przestanie kolidować (lustro helpera z form-panelu). */
function uniqueSlug(base: string, existing: readonly string[]): string {
  if (!existing.includes(base)) return base;
  let i = 2;
  let candidate: string;
  do {
    const suffix = `_${i}`;
    candidate = `${base.slice(0, SLUG_MAX_LENGTH - suffix.length)}${suffix}`;
    i++;
  } while (existing.includes(candidate));
  return candidate;
}

/** Wolna (case-insensitive) nazwa wśród `existing`; przy kolizji dokleja „ (2)", „ (3)"… */
function uniqueViewName(base: string, existing: readonly string[]): string {
  const lower = existing.map((n) => n.trim().toLowerCase());
  if (!lower.includes(base.trim().toLowerCase())) return base;
  let i = 2;
  let candidate = `${base} (${i})`;
  while (lower.includes(candidate.trim().toLowerCase())) {
    i += 1;
    candidate = `${base} (${i})`;
  }
  return candidate;
}

interface KartotekaListViewsPanelProps {
  /** Tożsamość kartoteki (Faza 2): systemowa (slug) lub Kreatora (UUID encji). */
  identity: KartotekaIdentityRef;
  /** Pola kartoteki — paleta „Dodaj kolumnę" w builderze widoku listy. */
  fields: KartotekaBuilderField[];
  /**
   * Czy `fields` jest katalogiem POTWIERDZONYM (zapytanie definicji ma dane), nie
   * fallbackiem `[]` w trakcie ładowania — forward do `KartotekaViewListBuilder`, żeby
   * soft-heal w `ListColumnsPanel` nie wycinał kolumn zanim katalog się wczyta (patrz
   * JSDoc `availableFields` w `KartotekaViewListBuilder`).
   */
  fieldsReady: boolean;
  viewsQuery: UseQueryResult<KartotekaViewDto[]>;
  readOnly: boolean;
  /** Domyślny `ListConfig` kartoteki — seed nowego / duplikowanego widoku listy. */
  defaultListConfig?: KartotekaDefaultListConfig;
}

export function KartotekaListViewsPanel({
  identity,
  fields,
  fieldsReady,
  viewsQuery,
  readOnly,
  defaultListConfig,
}: KartotekaListViewsPanelProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingView, setEditingView] = useState<KartotekaViewDto | null>(null);
  const [renamingSlug, setRenamingSlug] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<KartotekaViewDto | null>(null);

  const invalidateViews = () =>
    queryClient.invalidateQueries({ queryKey: kartotekaLibraryViewsQueryKey(identity) });

  /** Config listy do zapisu — istniejący widoku, w razie braku domyślny kartoteki. */
  const listConfigOf = (view: KartotekaViewDto): Record<string, unknown> =>
    (view.config as Record<string, unknown> | null) ??
    (defaultListConfig as unknown as Record<string, unknown>) ??
    {};

  const renameMutation = useMutation({
    mutationFn: ({ view, name }: { view: KartotekaViewDto; name: string }) =>
      updateKartotekaLibraryView(identity, view.slug, {
        name,
        viewType: 'list',
        config: listConfigOf(view),
        sortOrder: view.sortOrder,
      }),
    onSuccess: async () => {
      toast.show({
        variant: 'success',
        title: t('moduleTemplateEditor.referencePanel.renameListViewSuccess'),
      });
      await invalidateViews();
      setRenamingSlug(null);
      setRenameError(null);
    },
    onError: (err) => {
      const code = isAxiosError(err)
        ? (err.response?.data?.error as string | undefined)
        : undefined;
      toast.show({
        variant: 'error',
        title:
          code === 'NAME_TAKEN'
            ? t('moduleTemplateEditor.referencePanel.viewNameTaken')
            : t('moduleTemplateEditor.referencePanel.renameListViewError'),
      });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (source: KartotekaViewDto) => {
      const all = viewsQuery.data ?? [];
      const allSlugs = all.map((v) => v.slug);
      const listNames = all.filter((v) => v.viewType === 'list').map((v) => v.name);
      const name = uniqueViewName(`${source.name} (kopia)`, listNames);
      const slug = uniqueSlug(nameToKartotekaViewSlug(name), allSlugs);
      const clonedConfig = source.config
        ? (JSON.parse(JSON.stringify(source.config)) as Record<string, unknown>)
        : listConfigOf(source);
      return createKartotekaLibraryView(identity, {
        slug,
        name,
        viewType: 'list',
        config: clonedConfig,
        sortOrder: source.sortOrder,
      });
    },
    onSuccess: async () => {
      toast.show({
        variant: 'success',
        title: t('moduleTemplateEditor.referencePanel.duplicateListViewSuccess'),
      });
      await invalidateViews();
    },
    onError: (err) => {
      const code = isAxiosError(err)
        ? (err.response?.data?.error as string | undefined)
        : undefined;
      toast.show({
        variant: 'error',
        title:
          code === 'NAME_TAKEN'
            ? t('moduleTemplateEditor.referencePanel.viewNameTaken')
            : t('moduleTemplateEditor.referencePanel.duplicateListViewError'),
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (view: KartotekaViewDto) => deleteKartotekaLibraryView(identity, view.slug),
    onSuccess: async () => {
      toast.show({
        variant: 'success',
        title: t('moduleTemplateEditor.referencePanel.deleteListViewSuccess'),
      });
      await invalidateViews();
    },
    onError: (err) => {
      const code = isAxiosError(err)
        ? (err.response?.data?.error as string | undefined)
        : undefined;
      const messageKey =
        code === 'KARTOTEKA_VIEW_PUBLISHED_CANNOT_DELETE'
          ? 'moduleTemplateEditor.referencePanel.deleteViewPublishedError'
          : 'moduleTemplateEditor.referencePanel.deleteListViewError';
      toast.show({ variant: 'error', title: t(messageKey) });
    },
  });

  if (editingView) {
    return (
      <KartotekaViewListBuilder
        identity={identity}
        view={editingView}
        fields={fields}
        fieldsReady={fieldsReady}
        defaultListConfig={defaultListConfig}
        onBack={() => setEditingView(null)}
      />
    );
  }

  if (viewsQuery.isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-3/4" />
      </div>
    );
  }

  if (viewsQuery.isError) {
    return (
      <p className="text-sm text-gray-400 dark:text-gray-500">
        {t('moduleTemplateEditor.referencePanel.viewsLoadError')}
      </p>
    );
  }

  const allViews = viewsQuery.data ?? [];
  const listViews = allViews.filter((v) => v.viewType === 'list');
  const allSlugs = allViews.map((v) => v.slug);

  return (
    <div>
      {!readOnly && (
        <div className="mb-3 flex justify-end">
          <Button
            size="sm"
            variant="secondary"
            data-testid="kartoteka-new-list-view-btn"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="mr-1 h-4 w-4" />
            {t('moduleTemplateEditor.referencePanel.newListViewButton')}
          </Button>
        </div>
      )}

      {listViews.length === 0 ? (
        <p className="text-sm text-text-muted">
          {t('moduleTemplateEditor.referencePanel.noListViews')}
        </p>
      ) : (
        <ul className="space-y-2">
          {listViews.map((v) => {
            const isRenaming = renamingSlug === v.slug;
            const isPublished = v.firstPublishedAt !== null;
            const commitRename = () => {
              const trimmed = renameValue.trim();
              if (!trimmed || trimmed === v.name) {
                setRenamingSlug(null);
                setRenameError(null);
                return;
              }
              // Unikalność nazwy tylko wśród widoków listy (osobna przestrzeń niż layouty).
              const dup = listViews.some(
                (o) => o.slug !== v.slug && o.name.trim().toLowerCase() === trimmed.toLowerCase(),
              );
              if (dup) {
                setRenameError(t('moduleTemplateEditor.referencePanel.viewNameTaken'));
                return;
              }
              setRenameError(null);
              renameMutation.mutate({ view: v, name: trimmed });
            };
            return (
              <li
                key={v.id}
                className="flex flex-wrap items-center gap-3 rounded-md border border-border-primary px-3 py-2 text-sm"
                data-testid="kartoteka-list-view-item"
              >
                {isRenaming ? (
                  <div className="flex w-full flex-col gap-1">
                    <Input
                      value={renameValue}
                      onChange={(e) => {
                        setRenameValue(e.target.value);
                        if (renameError) setRenameError(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitRename();
                        if (e.key === 'Escape') {
                          // Esc anuluje zmianę nazwy — „obsłużone", okno nadrzędne się nie zamyka.
                          e.preventDefault();
                          setRenamingSlug(null);
                          setRenameError(null);
                        }
                      }}
                      onBlur={commitRename}
                      className="max-w-xs flex-1"
                      autoFocus
                      data-testid="kartoteka-list-view-rename-input"
                    />
                    {renameError && <p className="text-xs text-red-500">{renameError}</p>}
                  </div>
                ) : (
                  <span className="flex min-w-0 flex-1 items-center gap-1.5">
                    <span className="truncate font-medium text-text-primary">{v.name}</span>
                    <code className="shrink-0 text-xs text-gray-400 dark:text-gray-500">
                      {v.slug}
                    </code>
                    {!readOnly && (
                      <Tooltip content={t('moduleTemplateEditor.referencePanel.renameButton')}>
                        <IconButton
                          size="sm"
                          variant="ghost"
                          aria-label={t('moduleTemplateEditor.referencePanel.renameButton')}
                          data-testid="kartoteka-list-view-rename-btn"
                          onClick={() => {
                            setRenamingSlug(v.slug);
                            setRenameValue(v.name);
                            setRenameError(null);
                          }}
                        >
                          <Pencil />
                        </IconButton>
                      </Tooltip>
                    )}
                    {isPublished && (
                      <Badge color="green">
                        {t('moduleTemplateEditor.referencePanel.publishedBadge')}
                      </Badge>
                    )}
                  </span>
                )}
                {!readOnly && !isRenaming && (
                  <div className="ml-auto flex shrink-0 items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="secondary"
                      leftIcon={<Eye className="h-3.5 w-3.5" />}
                      data-testid="kartoteka-list-view-edit-btn"
                      onClick={() => setEditingView(v)}
                    >
                      {t('moduleTemplateEditor.referencePanel.editListViewButton')}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      leftIcon={<Copy className="h-3.5 w-3.5" />}
                      disabled={duplicateMutation.isPending}
                      data-testid="kartoteka-list-view-duplicate-btn"
                      onClick={() => duplicateMutation.mutate(v)}
                    >
                      {t('moduleTemplateEditor.referencePanel.duplicateListButton')}
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                      disabled={isPublished}
                      title={
                        isPublished
                          ? t('moduleTemplateEditor.referencePanel.deleteListViewPublishedHint')
                          : undefined
                      }
                      data-testid="kartoteka-list-view-delete-btn"
                      onClick={() => setPendingDelete(v)}
                    >
                      {t('moduleTemplateEditor.referencePanel.deleteListViewButton')}
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <CreateKartotekaViewModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        identity={identity}
        existingSlugs={allSlugs}
        existingNames={listViews.map((v) => v.name)}
        viewType="list"
        config={(defaultListConfig as unknown as Record<string, unknown>) ?? { columns: [] }}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        variant="danger"
        title={t('moduleTemplateEditor.referencePanel.deleteListViewConfirmTitle')}
        message={
          pendingDelete
            ? t('moduleTemplateEditor.referencePanel.deleteListViewConfirmMessage', {
                name: pendingDelete.name,
              })
            : ''
        }
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (pendingDelete) {
            deleteMutation.mutate(pendingDelete);
            setPendingDelete(null);
          }
        }}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}
